import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { getHandoverPack, exportHandoverPackPdf } from 'services/handoverService'
import { downloadBlob } from 'utils/downloadBlob'
import SharedBackLink from 'components/BackLink'
import { ROLE_LABELS } from 'utils/roles'

const STATUS_META = {
  ready:     { tint: '#15803d', Icon: CheckCircleRoundedIcon },
  attention: { tint: '#d97706', Icon: WarningAmberRoundedIcon },
  blocked:   { tint: '#b00020', Icon: ErrorRoundedIcon },
}

const readinessTint = v =>
  v === null || v === undefined ? '#9aa0aa'
  : v >= 90 ? '#15803d'
  : v >= 60 ? '#4d9e2a'
  : v >= 35 ? '#d97706'
  : '#b00020'

const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null

const RAG = {
  green: { tint: '#15803d', label: 'Current' },
  amber: { tint: '#d97706', label: 'Expiring' },
  red:   { tint: '#b00020', label: 'Expired' },
  grey:  { tint: '#9aa0aa', label: 'On file' },
}

const docRag = expiresAt => {
  if (!expiresAt) return 'grey'
  const due = new Date(expiresAt)
  const now = new Date()
  if (due < now) return 'red'
  const soon = new Date()
  soon.setDate(soon.getDate() + 30)
  return due <= soon ? 'amber' : 'green'
}

const ExitReadinessPage = () => {
  const { bpId } = useParams()
  const navigate = useNavigate()
  const [pack, setPack] = useState(null)
  const [error, setError] = useState(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    getHandoverPack(bpId)
      .then(res => setPack(res?.data?.data || null))
      .catch(err => setError(err?.response?.status === 403
        ? `Only an ${ROLE_LABELS.owner} of this building can open its handover pack.`
        : 'Could not load handover data.'))
  }, [bpId])

  const handleExportPdf = async () => {
    if (exporting) return
    setExporting(true)
    try {
      const { data, headers } = await exportHandoverPackPdf(bpId)
      const disposition = headers?.['content-disposition'] || ''
      const match = disposition.match(/filename="?([^"]+)"?/i)
      const filename = match?.[1] || `handover-pack-${new Date().toISOString().slice(0, 10)}.pdf`
      const blob = data instanceof Blob ? data : new Blob([data], { type: 'application/pdf' })
      downloadBlob(blob, filename)
    } catch (err) {
      toast.error('Could not export the handover pack.')
    } finally {
      setExporting(false)
    }
  }

  if (error) return <Page><Empty>{error}</Empty></Page>
  if (!pack) return <Page><Empty>Assessing exit readiness…</Empty></Page>

  const { building, readiness, documents, assets, openActions, openTasks, openPermits, openIncidents } = pack
  const pct = readiness?.readiness
  const tint = readinessTint(pct)
  const openTotal = (openActions?.length || 0) + (openTasks?.length || 0)
    + (openPermits?.length || 0) + (openIncidents?.length || 0)

  return (
    <Page>
      <Toolbar>
        <BackBtn onClick={() => navigate(`/bp-list/${bpId}`)}>Back to Building Passport</BackBtn>
        <PrintBtn onClick={handleExportPdf} disabled={exporting} data-tour-id='handover.download-btn'>
          <DownloadRoundedIcon style={{ fontSize: 15 }} />
          {exporting ? 'Preparing PDF…' : 'Download PDF'}
        </PrintBtn>
      </Toolbar>

      <Sheet>
        <SheetHead>
          <HeadText>
            <Eyebrow>Exit readiness · handover pack</Eyebrow>
            <Title>{building?.name}</Title>
            <Address>
              {[building?.street, building?.city, building?.postalCode].filter(Boolean).join(', ')}
              {building?.uprn ? ` · UPRN ${building.uprn}` : ''}
            </Address>
          </HeadText>
          <ScoreRing $tint={tint} data-tour-id='handover.readiness-score'>
            <ScoreValue $tint={tint}>{pct === null || pct === undefined ? '—' : Math.round(pct)}</ScoreValue>
            <ScoreUnit>% ready</ScoreUnit>
          </ScoreRing>
        </SheetHead>

        <SectionTitle>Readiness by category</SectionTitle>
        <CategoryGrid>
          {(readiness?.categories || []).map(cat => {
            const meta = STATUS_META[cat.status] || STATUS_META.attention
            const Icon = meta.Icon
            return (
              <CategoryCard key={cat.key} $tint={meta.tint}>
                <CategoryHead>
                  <Icon style={{ fontSize: 17, color: meta.tint }} />
                  <CategoryName>{cat.label}</CategoryName>
                  <CategoryScore $tint={meta.tint}>{Math.round(cat.score)}%</CategoryScore>
                </CategoryHead>
                <CategoryDetail>{cat.detail}</CategoryDetail>
              </CategoryCard>
            )
          })}
        </CategoryGrid>

        <SectionTitle>Document inventory · {(documents || []).reduce((a, d) => a + (d.count || 0), 0)} files</SectionTitle>
        {(documents || []).map(group => (
          <DocGroup key={group.category}>
            <DocGroupHead>
              {group.category}
              <DocGroupCount>{group.count}</DocGroupCount>
            </DocGroupHead>
            <DocTable>
              <tbody>
                {(group.files || []).map((f, i) => {
                  const rag = RAG[docRag(f.expiresAt)]
                  return (
                    <tr key={i}>
                      <RagCell><RagBadge $tint={rag.tint}>{rag.label}</RagBadge></RagCell>
                      <DocCell>{f.name}</DocCell>
                      <DocCellMuted>{f.type}</DocCellMuted>
                      <DocCellRight $expired={f.expiresAt && new Date(f.expiresAt) < new Date()}>
                        {f.expiresAt ? `expires ${fmtDate(f.expiresAt)}` : ''}
                      </DocCellRight>
                    </tr>
                  )
                })}
              </tbody>
            </DocTable>
          </DocGroup>
        ))}

        <SectionTitle>Asset register · {(assets || []).length}</SectionTitle>
        {(assets || []).length === 0 ? (
          <EmptyLine>No assets recorded.</EmptyLine>
        ) : (
          <DocTable>
            <tbody>
              {assets.map((a, i) => (
                <tr key={i}>
                  <DocCellMuted style={{ width: 160 }}>{a.type}</DocCellMuted>
                  <DocCell>{a.name}{a.location ? ` — ${a.location}` : ''}</DocCell>
                  <DocCellRight>{a.filesCount} linked file{a.filesCount === 1 ? '' : 's'}</DocCellRight>
                </tr>
              ))}
            </tbody>
          </DocTable>
        )}

        <SectionTitle $accent={openTotal > 0 ? '#b00020' : undefined} data-tour-id='handover.open-items'>
          Live items transferring with the building · {openTotal}
        </SectionTitle>
        {openTotal === 0 ? (
          <EmptyLine>Nothing open — remedials, tasks, permits and incidents are all closed out.</EmptyLine>
        ) : (
          <>
            {(openActions || []).length > 0 && (
              <OpenBlock>
                <OpenHead>Open remedials · {openActions.length}</OpenHead>
                {openActions.map((a, i) => (
                  <OpenRow key={i}>
                    <OpenSeverity>{a.severity}</OpenSeverity>
                    <span>{a.title}</span>
                    {a.suggestedDueBy && <OpenDue>due {fmtDate(a.suggestedDueBy)}</OpenDue>}
                  </OpenRow>
                ))}
              </OpenBlock>
            )}
            {(openTasks || []).length > 0 && (
              <OpenBlock>
                <OpenHead>Incomplete tasks · {openTasks.length}</OpenHead>
                {openTasks.map((t, i) => (
                  <OpenRow key={i}>
                    <OpenSeverity>{(t.status || '').replace(/_/g, ' ')}</OpenSeverity>
                    <span>{t.title}</span>
                    {t.deadline && <OpenDue>due {fmtDate(t.deadline)}</OpenDue>}
                  </OpenRow>
                ))}
              </OpenBlock>
            )}
            {(openPermits || []).length > 0 && (
              <OpenBlock>
                <OpenHead>Permits not closed out · {openPermits.length}</OpenHead>
                {openPermits.map((p, i) => (
                  <OpenRow key={i}>
                    <OpenSeverity>{p.status}</OpenSeverity>
                    <span>{p.reference} — {p.title}</span>
                  </OpenRow>
                ))}
              </OpenBlock>
            )}
            {(openIncidents || []).length > 0 && (
              <OpenBlock>
                <OpenHead>Open incidents · {openIncidents.length}</OpenHead>
                {openIncidents.map((inc, i) => (
                  <OpenRow key={i}>
                    <OpenSeverity>{inc.severity}</OpenSeverity>
                    <span>
                      {inc.incidentType?.replace(/_/g, ' ')} · {inc.category}
                      {inc.occurredAt ? ` · ${fmtDate(inc.occurredAt)}` : ''}
                      {['pending', 'in_progress'].includes(inc.investigationStatus) ? ' · investigation open' : ''}
                    </span>
                  </OpenRow>
                ))}
              </OpenBlock>
            )}
          </>
        )}

        <Footnote>
          Generated {readiness?.generatedAt ? new Date(readiness.generatedAt).toLocaleString('en-GB') : ''} from live
          platform data. Document inventory reflects current (non-superseded) versions; compliance figures come from the
          scored obligation programme at generation time.
        </Footnote>
      </Sheet>
    </Page>
  )
}

export default ExitReadinessPage

const Page = styled.div`
  max-width: 880px;
  margin: 0 auto;
  padding: 24px 24px 64px;
  @media (max-width: 600px) { padding: 16px 12px 48px; }
`
const Toolbar = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 14px;
`
const BackBtn = styled(SharedBackLink)``
const PrintBtn = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px;
  background: #0C1229;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: default; }
`
const Sheet = styled.div`
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 32px 36px;
  @media (max-width: 600px) { padding: 20px 16px; }
  @media print { border: none; border-radius: 0; padding: 0; }
`
const SheetHead = styled.header`
  display: flex; align-items: center; justify-content: space-between; gap: 24px;
  border-bottom: 3px solid #0C1229;
  padding-bottom: 18px;
  margin-bottom: 20px;
`
const HeadText = styled.div` display: flex; flex-direction: column; gap: 4px; `
const Eyebrow = styled.span`
  font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase;
  color: #6b7280; font-weight: 800;
`
const Title = styled.h1`
  margin: 0;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 30px;
  letter-spacing: -0.7px;
  color: #0C1229;
`
const Address = styled.span` font-size: 12.5px; color: #6b7280; `
const ScoreRing = styled.div`
  width: 110px; height: 110px;
  border-radius: 50%;
  border: 5px solid ${({ $tint }) => $tint};
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  flex-shrink: 0;
`
const ScoreValue = styled.span`
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 34px;
  line-height: 1;
  color: ${({ $tint }) => $tint};
  font-variant-numeric: tabular-nums;
`
const ScoreUnit = styled.span`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
  color: #6b7280;
  margin-top: 4px;
`
const SectionTitle = styled.h2`
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  margin: 26px 0 10px;
  color: ${({ $accent }) => $accent || '#0C1229'};
`
const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`
const CategoryCard = styled.div`
  border: 1px solid #ececec;
  border-left: 4px solid ${({ $tint }) => $tint};
  border-radius: 10px;
  padding: 12px 14px;
  display: flex; flex-direction: column; gap: 4px;
`
const CategoryHead = styled.div` display: flex; align-items: center; gap: 8px; `
const CategoryName = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #0C1229;
`
const CategoryScore = styled.span`
  margin-left: auto;
  font-family: 'FuturaPT-Light', system-ui, sans-serif;
  font-size: 18px;
  color: ${({ $tint }) => $tint};
  font-variant-numeric: tabular-nums;
`
const CategoryDetail = styled.span`
  font-size: 11.5px;
  color: #6b7280;
  line-height: 1.45;
`
const DocGroup = styled.div` margin-bottom: 14px; `
const DocGroupHead = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 12.5px;
  font-weight: 800;
  color: #0C1229;
  margin-bottom: 4px;
`
const DocGroupCount = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f4f4f6;
  color: #6b7280;
`
const DocTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  tr { border-bottom: 1px solid #f4f4f6; }
`
const DocCell = styled.td`
  padding: 5px 8px 5px 0;
  font-size: 12px;
  color: #1e293b;
`
const RagCell = styled.td`
  padding: 5px 10px 5px 0;
  width: 92px;
`
const RagBadge = styled.span`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em;
  color: ${({ $tint }) => $tint};
  white-space: nowrap;
  &::before {
    content: '';
    width: 8px; height: 8px; border-radius: 50%;
    background: ${({ $tint }) => $tint};
    flex-shrink: 0;
  }
`
const DocCellMuted = styled.td`
  padding: 5px 8px 5px 0;
  font-size: 11.5px;
  color: #6b7280;
`
const DocCellRight = styled.td`
  padding: 5px 0;
  font-size: 11px;
  text-align: right;
  white-space: nowrap;
  color: ${({ $expired }) => $expired ? '#b00020' : '#6b7280'};
  font-weight: ${({ $expired }) => $expired ? 700 : 400};
`
const OpenBlock = styled.div` margin-bottom: 14px; `
const OpenHead = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: #0C1229;
  margin-bottom: 6px;
`
const OpenRow = styled.div`
  display: flex; align-items: baseline; gap: 10px;
  padding: 5px 0;
  border-bottom: 1px solid #f4f4f6;
  font-size: 12px;
  color: #1e293b;
`
const OpenSeverity = styled.span`
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #6b7280;
  min-width: 92px;
`
const OpenDue = styled.span`
  margin-left: auto;
  font-size: 11px;
  color: #6b7280;
  white-space: nowrap;
`
const EmptyLine = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #6b7280;
  font-style: italic;
`
const Empty = styled.div`
  padding: 48px;
  text-align: center;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  color: #6b7280;
`
const Footnote = styled.p`
  margin-top: 28px;
  font-size: 10.5px;
  color: #6b7280;
  border-top: 1px solid #ececec;
  padding-top: 12px;
  line-height: 1.5;
`
